<template>
  <form-fieldset :class="[isAlternativeMethodSupported && 'cf-flex cf-flex--column cf-flex--gap-16']">
    <div
      v-if="isLoading"
      class="cf-section-loader-container"
    >
      <div class="cf-loader" />
    </div>

    <cf-alt-payment-radio-buttons
      v-else-if="giftCardReloadPaymentMethod && (isGooglePayEnabled || isApplePayEnabled)"
      :can-use-google-pay="isGooglePayEnabled"
      :can-use-apple-pay="isApplePayEnabled"
      :force-show-apple-pay="giftCardReloadPaymentMethod.method === 'ApplePay'"
      :selected-payment-method="selectedPaymentMethod"
      @method-select="$emit('method-select', $event)"
    />

    <div
      v-show="!isLoading && !giftCardReloadPaymentMethod"
      class="cf-flex cf-flex--column cf-flex--gap-16"
    >
      <!--
      Previously only ShiftFour methods were being hidden when the `simplePayment` prop was true.
      I have no idea why, so this is something we should look out for.
      -->
      <cf-shift-four-payments-provider
        v-if="paymentGateway.providers['ShiftFour']"
        v-bind="{ ...$props, ...$attrs }"

        @set-loading-status="setLoadingStatus"
        @set-enabled-payment-method="setEnabledPaymentMethod"
      />

      <cf-square-payments-provider
        v-else-if="paymentGateway.providers['Square']"
        ref="alternativeMethods"
        v-bind="{ ...$props, ...$attrs }"

        @set-loading-status="setLoadingStatus"
        @set-enabled-payment-method="setEnabledPaymentMethod"
      />

      <cf-default-payments-provider
        v-else
        ref="alternativeMethods"
        v-bind="{ ...$props, ...$attrs }"

        @set-loading-status="setLoadingStatus"
        @set-enabled-payment-method="setEnabledPaymentMethod"
      />

      <button
        v-if="showRoomChargeButton"
        class="cf-button cf-button--lg cf-button--primary cf-button--fill"
        type="button"
        @click="chargeToRoomClickHandler"
      >
        {{ content.PaymentScreen.metadata.chargeRoomCta || 'Charge Room' }}
      </button>

      <!-- NOTE: The extra pay/table v-if check is to temporarily disable gift cards for P@T/O@T -->
      <button
        v-if="showGiftCardButton"
        class="cf-button cf-button--lg cf-button--black cf-button--fill"
        type="button"
        @click="addNewGiftCardClickHandler"
      >
        Gift Card
      </button>

      <template v-if="showHouseAccountButton">
        <button
          class="cf-button cf-button--lg cf-button--black cf-button--fill"
          type="button"
          :disabled="isHouseAccountOverThreshold"
          @click="chargeHouseAccountClickHandler"
        >
          Pay with House Account
        </button>

        <p
          v-if="isHouseAccountOverThreshold"
          class="cf-subtitle cf-subtitle--2 cf-text-align-center"
        >
          {{ houseAccountThresholdMessage }}
        </p>
      </template>
    </div>

    <cf-title-separator v-if="paymentGateway.methods.creditCard && isAlternativeMethodSupported" />
  </form-fieldset>
</template>


<script>
  import { mapGetters } from 'vuex';

  import CfAltPaymentRadioButtons from './cf-alt-payment-radio-buttons/index.vue';
  import CfShiftFourPaymentsProvider from './cf-shift-four-payments-provider/index.vue';
  import CfSquarePaymentsProvider from './cf-square-payments-provider/index.vue';
  import CfDefaultPaymentsProvider from './cf-default-payments-provider/index.vue';

  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';


  export default {
    name: 'CfAlternativePaymentProviders',

    components: {
      CfAltPaymentRadioButtons,
      CfShiftFourPaymentsProvider,
      CfSquarePaymentsProvider,
      CfDefaultPaymentsProvider,
      CfTitleSeparator
    },

    props: {
      giftCardReloadPaymentMethod: {
        type: [Object, Boolean],
        default: null
      },

      hiddenElements: {
        type: Object,
        default: () => ({})
      },

      paymentGateway: {
        type: Object,
        required: true
      },

      selectedPaymentMethod: {
        type: String,
        required: true
      },

      showGiftCardButton: {
        type: Boolean,
        default: false
      },

      showRoomChargeButton: {
        type: Boolean,
        default: false
      },

      total: {
        type: Number,
        default: 0
      }
    },

    data() {
      return {
        isLoading: true,
        isApplePayEnabled: false,
        isGooglePayEnabled: false
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('orders', ['order', 'currentCapiOrderType']),
      ...mapGetters('session', ['currentUser', 'houseAccount', 'orderDetails', 'session']),

      isAlternativeMethodSupported() {
        return this.isApplePayEnabled
          || this.isGooglePayEnabled
          || this.showGiftCardButton
          || this.showRoomChargeButton
          || this.showHouseAccountButton;
      },

      houseAccountMetadata() {
        return (this.content && this.content.HouseAccounts && this.content.HouseAccounts.metadata) || {};
      },

      houseAccountThresholdMessage() {
        return this.houseAccountMetadata.exceedOrderThresholdText
          || 'You are over your threshold limit. Please contact support.';
      },

      showHouseAccountButton() {
        return !!this.paymentGateway.methods.houseAccount
          && !!this.session.isSignedIn
          && !!this.currentUser.accountId
          && !!this.houseAccount
          && this.orderDetails.orderType === 'catering';
      },

      isHouseAccountOverThreshold() {
        if (!this.houseAccount) return false;

        // HOUSE_ACCOUNT_TODO: the CAPI response doesn't currently include outstandingBalance,
        // so the total-limit check can't account for accrued unpaid orders. When that field
        // is added to GET /account/me/house-accounts, factor it in here.
        const total = Number(this.total) || 0;
        const totalLimit = Number(this.houseAccount.totalLimit);
        const limitPerOrder = Number(this.houseAccount.limitPerOrder);

        const exceedsPerOrder = Number.isFinite(limitPerOrder) && limitPerOrder > 0 && total > limitPerOrder;
        const exceedsTotal = Number.isFinite(totalLimit) && totalLimit > 0 && total > totalLimit;

        return exceedsPerOrder || exceedsTotal;
      }
    },

    methods: {
      setLoadingStatus(loadingStatus) {
        this.isLoading = loadingStatus;
      },

      addNewGiftCardClickHandler() {
        this.$openModal('addGiftCard', { paymentGateway: this.paymentGateway });
      },

      setEnabledPaymentMethod(methodType) {
        if (methodType === 'apple-pay') this.isApplePayEnabled = true;

        if (methodType === 'google-pay') this.isGooglePayEnabled = true;
      },

      chargeToRoomClickHandler() {
        this.$openModal('chargeToRoom', { hideSms: this.currentCapiOrderType === 'TipOnly' });
      },

      chargeHouseAccountClickHandler() {
        if (this.isHouseAccountOverThreshold || !this.houseAccount) return;

        this.$openModal('chargeHouseAccount');
      }
    }
  };
</script>
